import React, { useState, useEffect, useRef } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Sparkles, Loader2, CornerDownLeft } from "lucide-react";
import { useLang } from "@/i18n";

export function MagicInput() {
  const { t } = useLang();
  const [open, setOpen] = useState(false);
  const [text, setText] = useState("");
  const [loading, setLoading] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") { e.preventDefault(); setOpen(true); }
      if (e.key === "Escape") setOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  useEffect(() => { if (open) setTimeout(() => inputRef.current?.focus(), 50); }, [open]);

  const submit = async (e) => {
    e?.preventDefault();
    if (!text.trim() || loading) return;
    setLoading(true);
    try {
      const { data } = await api.post("/magic/create", { text: text.trim() });
      toast.success(data.message || t("Élément créé"));
      setText(""); setOpen(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || t("Impossible d'interpréter la demande"));
    } finally { setLoading(false); }
  };

  return (
    <>
      <button data-testid="magic-input-btn" onClick={() => setOpen(true)} title={t("Saisie Magique") + " (Ctrl+K)"}
        className="text-[#F2BC2E] hover:text-[#F10D19] transition-colors">
        <Sparkles size={18} />
      </button>

      {open && (
        <div className="fixed inset-0 z-[70] flex items-start justify-center pt-24 px-4" data-testid="magic-overlay">
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => setOpen(false)} />
          <form onSubmit={submit} className="relative w-full max-w-xl bg-[#141414] border border-[#F2BC2E]/40 rounded-2xl shadow-2xl overflow-hidden pg-fade-up">
            <div className="flex items-center gap-2 px-4 pt-4 pb-1 text-[#F2BC2E]">
              <Sparkles size={16} />
              <span className="text-xs font-bold uppercase tracking-wider">{t("Saisie Magique")}</span>
            </div>
            <div className="flex items-center gap-3 px-4 py-3">
              <input ref={inputRef} data-testid="magic-text-input" value={text} onChange={(e) => setText(e.target.value)}
                placeholder={t("Ex : Appeler Jean demain pour le devis de 5000$")}
                className="flex-1 bg-transparent text-white text-base placeholder-gray-600 focus:outline-none" />
              <button type="submit" disabled={loading || !text.trim()} data-testid="magic-submit-btn"
                className="btn-gold rounded-full px-4 py-2 text-sm flex items-center gap-2 disabled:opacity-40">
                {loading ? <Loader2 size={14} className="animate-spin" /> : <CornerDownLeft size={14} />} {t("Créer")}
              </button>
            </div>
            <div className="px-4 py-2.5 border-t border-white/5 text-[11px] text-gray-500">
              {t("L'IA crée automatiquement une tâche, un contact, un client ou une note.")}
            </div>
          </form>
        </div>
      )}
    </>
  );
}
